import React, { useState } from 'react';
import { getPlans, purchase, restore } from '../data/paymentService';
import { FiX, FiCheck, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { openExternal, TERMS_URL, PRIVACY_URL } from '../utils/openExternal';

const TRIGGER_HEADERS = {
  card_limit: "You've hit the free card limit",
  streak_freeze: 'Protect your streak',
  stats: 'Unlock advanced analytics',
  ai_generate: 'Generate unlimited decks with AI',
  generic: 'Upgrade to Premium',
};

const FEATURES = [
  'Unlimited flashcards',
  'Unlimited AI deck generation',
  'Unlimited streak freezes',
  'Advanced statistics & forecasts',
  'Priority support',
];

export default function PricingModal({ trigger = 'generic', onClose, onPurchaseComplete }) {
  const plans = getPlans();
  const [busy, setBusy] = useState(null); // tier string while purchase pending

  async function handlePurchase(tier) {
    if (busy) return;
    setBusy(tier);
    try {
      await purchase(tier);
      // Apple flow: the backend write happens in the appleProvider verified
      // handler; the UI refresh is triggered by usePayment listener.
      if (onPurchaseComplete) onPurchaseComplete(tier);
      toast.success('Welcome to Premium!');
      onClose?.();
    } catch (err) {
      // User dismissed the StoreKit sheet (any cancel code) — NOT an error.
      if (err?.message !== 'canceled' && !err?.cancelled) {
        toast.error(err?.message || 'Purchase failed. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleRestore() {
    try {
      await restore();
      toast.success('Restoring purchases…');
    } catch (err) {
      toast.error(err?.message || 'Could not restore purchases.');
    }
  }

  return (
    <div className="pricing-backdrop" onClick={onClose}>
      <div className="pricing-modal" onClick={e => e.stopPropagation()}>
        <button className="pricing-close" onClick={onClose} aria-label="Close">
          <FiX />
        </button>

        <div className="pricing-header">
          <div className="pricing-icon"><FiZap /></div>
          <h2>{TRIGGER_HEADERS[trigger] || TRIGGER_HEADERS.generic}</h2>
          <p className="pricing-subtitle">Choose the plan that works for you</p>
        </div>

        <div className="pricing-plans">
          {plans.map(plan => (
            <div
              key={plan.id}
              className={`pricing-plan ${plan.highlighted ? 'highlighted' : 'compact'}`}
            >
              {plan.badge && <div className="plan-badge">{plan.badge}</div>}
              <h3 className="plan-name">{plan.name}</h3>
              <div className="plan-price">
                <span className="plan-amount">{plan.price}</span>
                <span className="plan-period">{plan.period}</span>
              </div>
              {plan.perMonth && <div className="plan-permonth">{plan.perMonth}</div>}
              {plan.trial && <div className="plan-trial">{plan.trial}</div>}
              {plan.savings && <div className="plan-savings">{plan.savings}</div>}
              <button
                className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-secondary'} ${plan.highlighted ? 'btn-lg' : ''} plan-cta`}
                onClick={() => handlePurchase(plan.id)}
                disabled={Boolean(busy)}
              >
                {busy === plan.id
                  ? 'Processing…'
                  : (plan.highlighted ? (plan.trial ? plan.cta : 'Get Started') : (plan.cta || 'Choose Plan'))}
              </button>
            </div>
          ))}
        </div>

        <div className="pricing-features">
          <h4>All plans include:</h4>
          <ul>
            {FEATURES.map(f => (
              <li key={f}><FiCheck className="feature-check" /> {f}</li>
            ))}
          </ul>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
          <button className="btn-link" onClick={handleRestore} type="button">
            Restore purchases
          </button>
        </div>

        {/* Subscription EULA / Privacy — required by Apple Guideline 3.1.2(c) for any app
            with auto-renewable subscriptions. MUST appear in-app AND in the App Store
            description. See shared-docs/in-app-purchases.md "Subscription EULA". */}
        <p className="pricing-legal">
          {/* Apple 3.1.2(c): the price after the trial MUST be explicit. */}
          {plans[0]?.trial
            ? `1 month free, then ${plans[0].price}/year. Auto-renews until canceled. Manage in iOS Settings.`
            : 'Auto-renews until canceled. Manage in iOS Settings.'}
          {' '}
          <a href={TERMS_URL} onClick={(e) => { e.preventDefault(); openExternal(TERMS_URL); }}>Terms of Use (EULA)</a>
          {' · '}
          <a href={PRIVACY_URL} onClick={(e) => { e.preventDefault(); openExternal(PRIVACY_URL); }}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
